const STORAGE_KEY = 'preferredThemeMode';
// 'auto' sigue el tema claro/oscuro del navegador o del sistema operativo,
// resolviendo siempre a 'light' o 'graphite' (nunca a 'blue'/'dark', que son
// paletas elegidas a mano).
export const THEME_MODES = ['auto', 'graphite', 'blue', 'dark', 'light'];
const DEFAULT_MODE = 'auto';

// Avisa a la app nativa Android (WebView + puente JS "Android" en MainActivity.kt)
// para que la status bar / navigation bar del sistema sigan el tema actual.
// No existe fuera del WebView empaquetado, por eso el chequeo de window.Android.
const notifyNativeApp = (mode) => {
  if (typeof window !== 'undefined' && window.Android?.setThemeMode) {
    window.Android.setThemeMode(mode);
  }
};

const prefersDarkMediaQuery = () =>
  (typeof window !== 'undefined' && window.matchMedia)
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

// Desde Android 13, el WebView resuelve `prefers-color-scheme` según el
// atributo isLightTheme del theme de la Activity (no según el tema real del
// sistema) y por defecto da 'light' si ese atributo no está declarado — o
// sea que el matchMedia de abajo puede no reflejar nunca el tema real dentro
// del WebView empaquetado. Por eso el valor que informa Android por
// window.setNativeThemeMode manda por sobre matchMedia cuando existe. Se
// persiste en sessionStorage (no solo en memoria) porque este mismo servicio
// puede disparar un window.location.reload() al recibirlo: sin persistirlo,
// el reload perdía el valor, la página recién cargada volvía a resolver mal
// por matchMedia, Android volvía a corregir, y volvía a recargar — loop.
const NATIVE_DARK_STORAGE_KEY = 'nativeIsDarkMode';

class ThemeService {
  constructor() {
    const stored = localStorage.getItem(STORAGE_KEY);
    this.currentMode = THEME_MODES.includes(stored) ? stored : DEFAULT_MODE;

    const storedNative = sessionStorage.getItem(NATIVE_DARK_STORAGE_KEY);
    this.nativeIsDark = storedNative === null ? null : storedNative === 'true';
    notifyNativeApp(this.getResolvedMode());

    // Puente Android -> JS: MainActivity.kt puede llamar
    // window.setNativeThemeMode(isDark) (via evaluateJavascript) cada vez
    // que cambia el tema del sistema, en vez de depender de que la WebView
    // reporte bien prefers-color-scheme. Si estamos en 'auto' y el tema
    // resuelto cambia, recargamos para recalcular la paleta (mismo patrón
    // que el listener de matchMedia de abajo).
    if (typeof window !== 'undefined') {
      window.setNativeThemeMode = (isDark) => {
        const resolvedBefore = this.getResolvedMode();
        this.nativeIsDark = !!isDark;
        sessionStorage.setItem(NATIVE_DARK_STORAGE_KEY, String(this.nativeIsDark));
        if (this.currentMode === 'auto' && this.getResolvedMode() !== resolvedBefore) {
          window.location.reload();
        }
      };
    }

    // En modo 'auto', si el usuario cambia el tema del sistema/navegador
    // mientras la app está abierta, recargamos para recalcular la paleta
    // (mismo patrón que el selector manual en PreferenciasTema, que también
    // recarga al cambiar de tema). Se ignora si ya tenemos un valor nativo
    // explícito: ese manda por sobre prefers-color-scheme.
    const mq = prefersDarkMediaQuery();
    if (mq) {
      const onSystemThemeChange = () => {
        if (this.currentMode === 'auto' && this.nativeIsDark === null) {
          window.location.reload();
        }
      };
      if (mq.addEventListener) {
        mq.addEventListener('change', onSystemThemeChange);
      } else if (mq.addListener) {
        mq.addListener(onSystemThemeChange);
      }
    }
  }

  getMode() {
    return this.currentMode;
  }

  // Modo real a aplicar: si el usuario eligió 'auto', se resuelve contra el
  // tema informado por Android (si existe ese puente) o si no, contra el
  // tema claro/oscuro del navegador/sistema operativo.
  getResolvedMode() {
    if (this.currentMode !== 'auto') return this.currentMode;
    if (this.nativeIsDark !== null) return this.nativeIsDark ? 'graphite' : 'light';
    const mq = prefersDarkMediaQuery();
    return mq?.matches ? 'graphite' : 'light';
  }

  setMode(mode) {
    if (!THEME_MODES.includes(mode)) return;
    this.currentMode = mode;
    localStorage.setItem(STORAGE_KEY, mode);
    notifyNativeApp(this.getResolvedMode());
  }
}

const themeService = new ThemeService();
export default themeService;
